import { Avatar, Box, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EmailIcon from '@material-ui/icons/Email';
import GitHubIcon from '@material-ui/icons/GitHub';
import ChatIcon from '@material-ui/icons/Chat';
import EditIcon from '@material-ui/icons/Edit';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ANNOTATION_EDIT_URL, UserEntity } from '@backstage/catalog-model';
import { InfoCard, Link } from '@backstage/core-components';
import { EntityUserManagerChain } from '../EntityUserManagerChain/EntityUserManagerChain';

/**
 * Profile-page hero for User entity pages — large avatar, name, role line,
 * location/timezone, manager link, and a row of contact icons. Replaces the
 * stock EntityUserProfileCard's compact-card pattern with a full-width
 * presentation more appropriate to "this is who this person is".
 *
 * Backstage-native cost is bounded to the avatar element only — every other
 * primitive (InfoCard, Link, theme tokens, Material UI typography) uses the
 * project's standard idioms. The avatar uses MUI's Avatar with explicit 96px
 * dimensions because @backstage/ui Avatar maxes at x-large (48px), which is
 * too small for the hero pattern. Documented as a deliberate deviation on
 * PAT-448 and in the PR description.
 */

// Slack workspace ID for deep-linking.
const SLACK_WORKSPACE_URL_PREFIX = 'https://arrive.enterprise.slack.com/team/';

const HERO_AVATAR_PX = 96;

const useStyles = makeStyles(theme => ({
  card: {},
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  avatar: {
    width: HERO_AVATAR_PX,
    height: HERO_AVATAR_PX,
    fontSize: '2rem',
    flexShrink: 0,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.5),
  },
  role: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
  },
  meta: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  managerLine: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  contactButton: {
    color: theme.palette.text.secondary,
  },
}));

/** Returns the GMT/UTC offset string for an IANA timezone at the current time. */
function getTimezoneOffset(tz: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** "Europe/London (GMT+1)" or undefined if tz is missing/invalid. */
function formatTimezone(tz: string | undefined): string | undefined {
  if (!tz) return undefined;
  const offset = getTimezoneOffset(tz);
  return offset ? `${tz} (${offset})` : tz;
}

/** Initials fallback for the avatar (max 2 chars). */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w[0])
    .join('')
    .toLocaleUpperCase('en-US')
    .slice(0, 2);
}

export function EntityUserHero() {
  const classes = useStyles();
  const { entity } = useEntity();

  const annotations = entity.metadata.annotations ?? {};
  const profile = (entity as UserEntity).spec?.profile;

  const displayName = profile?.displayName ?? entity.metadata.name;
  const email = profile?.email;
  const picture = profile?.picture;

  const role = annotations['user/role'];
  const department = annotations['user/department'];
  const office = annotations['user/office'];
  const country = annotations['user/country'];
  const timezone = annotations['user/timezone'];
  const githubUser = annotations['github.com/user-login'];
  const slackId = annotations['slack.com/user-id'];
  const managerRef = annotations['user/manager'];
  const editUrl = annotations[ANNOTATION_EDIT_URL];

  const tzDisplay = formatTimezone(timezone);

  // "Role · Department" — both optional, join with separator when both present.
  const roleLine = [role, department].filter(Boolean).join(' · ');

  // "Office, Country · GMT+1" — flexible omit-when-missing.
  const locationParts: string[] = [];
  if (office && country) locationParts.push(`${office}, ${country}`);
  else if (office) locationParts.push(office);
  else if (country) locationParts.push(country);
  if (tzDisplay) locationParts.push(tzDisplay);
  const locationLine = locationParts.join(' · ');

  return (
    <InfoCard className={classes.card} variant="gridItem">
      <Box className={classes.row}>
        <Avatar src={picture} className={classes.avatar}>
          {initials(displayName)}
        </Avatar>
        <Box className={classes.info}>
          <Typography className={classes.name} component="h1">
            {displayName}
          </Typography>
          {roleLine && (
            <Typography className={classes.role}>{roleLine}</Typography>
          )}
          {locationLine && (
            <Typography className={classes.meta}>{locationLine}</Typography>
          )}
          {managerRef && (
            <Box className={classes.managerLine}>
              <EntityUserManagerChain initialManagerRef={managerRef} />
            </Box>
          )}
          <Box className={classes.contactRow}>
            {email && (
              <Tooltip title={`Email ${email}`}>
                <IconButton
                  size="small"
                  className={classes.contactButton}
                  component={Link}
                  to={`mailto:${email}`}
                  aria-label={`Email ${email}`}
                >
                  <EmailIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {githubUser && (
              <Tooltip title={`GitHub @${githubUser}`}>
                <IconButton
                  size="small"
                  className={classes.contactButton}
                  component={Link}
                  to={`https://github.com/${encodeURIComponent(githubUser)}`}
                  aria-label={`GitHub profile @${githubUser}`}
                >
                  <GitHubIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {slackId && (
              <Tooltip title="Message on Slack">
                <IconButton
                  size="small"
                  className={classes.contactButton}
                  component={Link}
                  to={`${SLACK_WORKSPACE_URL_PREFIX}${encodeURIComponent(slackId)}`}
                  aria-label={`Message ${displayName} on Slack`}
                >
                  <ChatIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {editUrl && (
              <Tooltip title="Edit metadata">
                <IconButton
                  size="small"
                  className={classes.contactButton}
                  component={Link}
                  to={editUrl}
                  aria-label={`Edit ${displayName}'s metadata`}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </InfoCard>
  );
}
